import { Box, Chip, Collapse, FormControl, FormLabel, Slider, Stack } from "@mui/material";
import { AppFormField, AppFormGrid } from "../layout";
import { chartOptionInputSx, GRID_HALF } from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  detectGridPaddingPreset,
  formatGridMargin,
  GRID_PADDING_PRESETS,
  parseGridMargin,
  readGridPadding,
  type GridPaddingPresetId,
  type GridPaddingValues,
} from "./chartOptionLayout";

type Props = {
  values: Partial<GridPaddingValues>;
  disabled?: boolean;
  onChange: (next: GridPaddingValues) => void;
};

const SIDES: Array<{ key: keyof GridPaddingValues; labelKey: string }> = [
  { key: "top", labelKey: "chartOptions.pos.top" },
  { key: "right", labelKey: "chartOptions.pos.right" },
  { key: "bottom", labelKey: "chartOptions.pos.bottom" },
  { key: "left", labelKey: "chartOptions.pos.left" },
];

export default function ChartGridPaddingControl({ values, disabled, onChange }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.chartOptionPanels;
  const [manualCustom, setManualCustom] = useState(false);
  const current = readGridPadding(values);
  const preset = detectGridPaddingPreset(current);
  const activePreset: GridPaddingPresetId = manualCustom || preset === "custom" ? "custom" : preset;

  const applyPreset = (nextPreset: Exclude<GridPaddingPresetId, "custom">) => {
    setManualCustom(false);
    onChange(GRID_PADDING_PRESETS[nextPreset]);
  };

  const updateSide = (side: keyof GridPaddingValues, raw: number) => {
    setManualCustom(true);
    onChange({
      ...current,
      [side]: formatGridMargin(raw),
    });
  };

  const presetOptions: Array<{ id: GridPaddingPresetId; labelKey: string }> = [
    { id: "compact", labelKey: "chartOptions.gridPadding.presets.compact" },
    { id: "balanced", labelKey: "chartOptions.gridPadding.presets.balanced" },
    { id: "spacious", labelKey: "chartOptions.gridPadding.presets.spacious" },
    { id: "custom", labelKey: "chartOptions.gridPadding.presets.custom" },
  ];

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={styles.presetChipsRow(activePreset === "custom")}>
        {presetOptions.map(({ id, labelKey }) => (
          <Chip
            key={id}
            size="small"
            label={t(labelKey)}
            clickable
            disabled={disabled}
            color={activePreset === id ? "primary" : "default"}
            variant={activePreset === id ? "filled" : "outlined"}
            onClick={() => {
              if (id === "custom") {
                setManualCustom(true);
                return;
              }
              applyPreset(id);
            }}
            sx={styles.presetChip}
          />
        ))}
      </Stack>

      <Collapse in={activePreset === "custom"}>
        <AppFormGrid
          spacing="compact"
          alignItems="stretch"
          sx={styles.customGrid}
        >
          {SIDES.map(({ key, labelKey }) => (
            <AppFormField key={key} size={GRID_HALF}>
              <FormControl fullWidth margin="dense" size="small" sx={chartOptionInputSx}>
                <FormLabel
                  component="legend"
                  sx={styles.sideLabel}
                >
                  <span>{t(labelKey)}</span>
                  <Box component="span" sx={styles.sideValue}>
                    {formatGridMargin(parseGridMargin(current[key]))}
                  </Box>
                </FormLabel>
                <Slider
                  size="small"
                  value={parseGridMargin(current[key])}
                  min={0}
                  max={40}
                  step={1}
                  disabled={disabled}
                  onChange={(_, value) => updateSide(key, value as number)}
                  valueLabelDisplay="off"
                  sx={styles.slider}
                />
              </FormControl>
            </AppFormField>
          ))}
        </AppFormGrid>
      </Collapse>
    </Box>
  );
}
